"use client"

import { useState } from "react"

import { useInAdminConsole } from "@/components/admin/theme/admin-ui-provider"
import { useWorkspaceDarkLogo, useWorkspaceLogo } from "@/hooks/use-workspace-logo"
import { PORTAL_LOGO_BACKING_CLASS, portalLogos } from "@/lib/portal/portal-logo"
import { useShellTranslation } from "@/lib/shell/use-shell-translation"
import { cn } from "@/lib/utils"

/** The size and place of the logo in a header, in either mode. */
const SHAPE = "h-7 w-auto max-w-32 shrink-0 object-contain"

/**
 * The customer's logo in the page header, after the sidebar toggle and before
 * the breadcrumb (owner, 2026-10-28): the workspace's own, else its company's
 * (useWorkspaceLogo). Nothing when there is none, or when it fails to load.
 * Every header that draws <SidebarTrigger> renders this right after the
 * toggle's separator (header-workspace-logo-inventory.guard.spec.ts).
 *
 * ON A DARK SURFACE (owner, 2026-10-31; spec 3.4 rule 3): a dark logo the
 * customer uploaded, else a logo that needs nothing (a badge or a pale logo,
 * which the list names for itself), else the generated white copy, with
 * nothing behind it, and the white plate only when there is none of those,
 * or its file fails to load.
 * CSS picks between the two, because a JS read of the theme runs after
 * hydration and flashes the wrong one. These are drawn on `bg-canvas` and
 * `bg-background`, and white reads on those in every theme's dark mode:
 * lib/portal/__tests__/white-logo-surfaces.spec.ts works the numbers out of
 * the stylesheets, so a theme that broke that fails there.
 *
 * A CUSTOMER WHOSE ONLY LOGO IS A DARK ONE (owner, 2026-11-01). A pale or white logo is filed by its ink as a logo for dark, and only
 * the light logo writes the column, because the column feeds e-mail, where white on a white page would vanish. So it reaches here
 * with no light logo, and it still shows: this reads the portal's one decision (portalLogos) and the portal's chip, so the two cannot
 * disagree. On a light page the chip is the brand's text shade, or the theme builder's, which is never a fill white cannot show on,
 * and the portal's own numbers hold it (lib/portal/__tests__/white-logo-surfaces.spec.ts).
 *
 * The super-admin console shares PageShell and has no tenant: it shows none.
 * The HumanFirewall mark at the top of the sidebar is not this
 * (components/brand/sidebar-brand-mark.tsx).
 */
export function HeaderWorkspaceLogo() {
  const inConsole = useInAdminConsole()
  const logo = useWorkspaceLogo()
  const darkLogo = useWorkspaceDarkLogo()
  const { t } = useShellTranslation()
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set())
  const fail = (url: string) => setFailed((current) => new Set(current).add(url))
  if (inConsole || (logo && failed.has(logo))) return null

  const { light, dark } = portalLogos({ tenantLogo: logo, kitDarkLogo: darkLogo && !failed.has(darkLogo) ? darkLogo : null })
  if (!light.url || !dark.url) return null
  const { url: lightUrl } = light
  const { url: darkUrl } = dark

  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element -- a tenant upload or CDN URL, not a static asset */}
      <img
        data-testid="header-workspace-logo"
        src={lightUrl}
        alt={t("settings.companyLogo.alt")}
        className={cn("hidden", SHAPE, "sm:block dark:hidden", PORTAL_LOGO_BACKING_CLASS[light.backing])}
        onError={() => fail(lightUrl)}
      />
      {/* Exactly one of the pair is ever on screen, so each carries the alt: a screen reader hears the one that is. */}
      {/* eslint-disable-next-line @next/next/no-img-element -- a tenant upload or CDN URL, not a static asset */}
      <img
        data-testid="header-workspace-logo-dark"
        src={darkUrl}
        alt={t("settings.companyLogo.alt")}
        className={cn("hidden", SHAPE, "dark:sm:block", PORTAL_LOGO_BACKING_CLASS[dark.backing])}
        onError={() => fail(darkUrl)}
      />
    </>
  )
}
